import { useEffect, useRef, useState } from "react";
import { portalLink } from "../utils/portal";
import "./Header.css";

/* ── Small inline icons used inside the mini course cards ── */
const IconClock = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);

const IconVideo = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="M16 10l6-3v10l-6-3" />
  </svg>
);

const IconPlus = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const IconStar = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.2 22 12 18.27 5.8 22 7 14.14l-5-4.87 7.1-1.01L12 2z" />
  </svg>
);

const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const COURSE_ICONS = {
  clock: IconClock,
  video: IconVideo,
  plus: IconPlus,
  star: IconStar,
  check: IconCheck,
};

/* ── Mini course card data — mirrors the full program cards, condensed ── */
const COURSE_CARDS = [
  {
    href: "/course-executive-architect",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80",
    imageAlt: "Executive Architect Program",
    imageTag: "100% Placement Assured",
    badge: "Flagship Program",
    title: "Executive Architect Program",
    meta: [
      { icon: "clock", text: "6 Months" },
      { icon: "video", text: "Live + Practical" },
    ],
    desc: "Become an industry-ready architect through live projects, BIM workflows, Architecture Services and placement support.",
    features: [
      { icon: "plus", text: "Tailored Learning Path" },
      { icon: "video", text: "Live Project Enrolment" },
      { icon: "star", text: "BIM Architecture + AI Render Tools" },
      { icon: "check", text: "100% Placement Aid" },
    ],
    builtFor: ["B.Arch Students", "Fresh Graduates", "Junior Architects"],
    cta: "View Program",
  },
  {
    href: "/course-executive-interior",
    image:
      // "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=600&q=80",
       "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=700&q=75&quot;",
    imageAlt: "Executive Interior Designer",
    imageTag: "100% Placement Assistance",
    badge: "Flagship Program",
    title: "Executive Interior Designer",
    meta: [
      { icon: "clock", text: "5 Months" },
      { icon: "video", text: "15 hrs/week" },
    ],
    desc: "We build the path from imagination to execution and open doors to become part of world-renowned organizations.",
    features: [
      { icon: "plus", text: "National + Intl. Expert Trainers" },
      { icon: "video", text: "Live Project Enrolment" },
      { icon: "star", text: "250 Hours Live Sessions" },
      { icon: "check", text: "100% Placement Assistance" },
    ],
    builtFor: ["B.Des Graduates", "Diploma Holders", "Design Aspirants"],
    cta: "View Program",
  },
  {
    href: "/customise-your-program",
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=600&q=80",
    imageAlt: "Customize Your Program",
    imageTag: "100% Placement Assistance",
    badge: "Build Your Own",
    title: "Customize Your Program",
    meta: [
      { icon: "clock", text: "As Per Module" },
      { icon: "video", text: "15 hrs/week" },
    ],
    desc: "Unlock your potential. Upgrade your skills and become deserving of the next position — a training path designed around you.",
    features: [
      { icon: "plus", text: "Career Counselling Included" },
      { icon: "video", text: "Live Projects, Assignment Based" },
      { icon: "star", text: "Choose From 15+ Modules" },
      { icon: "check", text: "100% Placement Assistance" },
    ],
    builtFor: ["Professionals", "Career Switchers", "B.Arch + 1yr Exp."],
    cta: "Start Application",
  },
];

const COURSE_ROUTES = COURSE_CARDS.map((course) => course.href);

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);
  const [hoveredCourse, setHoveredCourse] = useState(COURSE_CARDS[0]);
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [learningOpen, setLearningOpen] = useState(false);
  const [comingSoon, setComingSoon] = useState(null);
  const mobileSidebarRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const mobileProgramButtonRef = useRef(null);
  const mobileProgramPanelRef = useRef(null);

  useEffect(() => {
    if (!mobileOpen && !mobileProgramsOpen) {
      return undefined;
    }

    const scrollY = window.scrollY;
    const { body } = document;
    const root = document.documentElement;
    const previousStyles = {
      scrollBehavior: root.style.scrollBehavior,
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };

    root.style.scrollBehavior = "auto";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      root.style.scrollBehavior = "auto";
      body.style.overflow = previousStyles.overflow;
      body.style.position = previousStyles.position;
      body.style.top = previousStyles.top;
      body.style.width = previousStyles.width;
      window.scrollTo(0, scrollY);
      window.requestAnimationFrame(() => {
        root.style.scrollBehavior = previousStyles.scrollBehavior;
      });
    };
  }, [mobileOpen, mobileProgramsOpen]);

  useEffect(() => {
    if (!mobileOpen && !mobileProgramsOpen) {
      return undefined;
    }

    const handleOutsidePress = (event) => {
      const path = event.composedPath?.() || [];
      const clickedSidebar =
        mobileSidebarRef.current &&
        (mobileSidebarRef.current.contains(event.target) || path.includes(mobileSidebarRef.current));
      const clickedButton =
        mobileButtonRef.current &&
        (mobileButtonRef.current.contains(event.target) || path.includes(mobileButtonRef.current));
      const clickedProgramButton =
        mobileProgramButtonRef.current &&
        (mobileProgramButtonRef.current.contains(event.target) || path.includes(mobileProgramButtonRef.current));
      const clickedProgramPanel =
        mobileProgramPanelRef.current &&
        (mobileProgramPanelRef.current.contains(event.target) || path.includes(mobileProgramPanelRef.current));

      if (!clickedSidebar && !clickedButton && !clickedProgramButton && !clickedProgramPanel) {
        setMobileOpen(false);
        setMobileProgramsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsidePress);
    document.addEventListener("touchstart", handleOutsidePress);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePress);
      document.removeEventListener("touchstart", handleOutsidePress);
    };
  }, [mobileOpen, mobileProgramsOpen]);

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setMobileProgramsOpen(false);
  };

  /* ── Track current page so the matching nav link stays "hover-lit" + underlined ── */
  const [currentPath, setCurrentPath] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentPath(window.location.pathname);
    }
  }, []);

  const isActive = (path) => currentPath === path;
  const isCoursesActive = COURSE_ROUTES.includes(currentPath);

  const openComingSoon = (event, type) => {
    event.preventDefault();
    const label = type === "trainer" ? "Trainer" : "Student";
    setLoginOpen(false);
    setComingSoon({
      title: `${label} Portal`,
      text: `Our ${label.toLowerCase()} portal service will be launching soon. Stay tuned!`,
    });
  };

  return (
    <>
      <header className="ipdi-header">
        <div className="wrapper">
          <div className="wrapper-1">
            <div className="row myrow">

              {/* ── LEFT: Logo only ── */}
              <div className="col-sm-8 col-lg-8">
                <ul>
                  <li>
                    <div className="logo">
                      <a href="/">
                        <p className="logo-text">IPD<span>I</span></p>
                      </a>
                    </div>
                  </li>
                </ul>
              </div>

              {/* ── CENTER: Nav links absolutely centered ── */}
              <ul className="center-nav">
                <li className="courses">
                  <a
                    href="/course-executive-architect"
                    className={isCoursesActive ? "active-link" : undefined}
                  >
                    Courses
                    <span className="chevron-wrap">
                      <svg
                        width="11"
                        height="7"
                        viewBox="0 0 12 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M1 1L6.15152 7L11 1" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </span>
                  </a>

                  {/* ── Compact dark dropdown, anchored right below the Courses link ── */}
                  <div className="courses-mega">
                    <ul className="courses-mega-list">
                      {COURSE_CARDS.map((course, index) => (
                        <li
                          className={`course-card-wrap${course.href === hoveredCourse.href ? " is-active" : ""}`}
                          key={course.href}
                          onMouseEnter={() => setHoveredCourse(course)}
                          onFocus={() => setHoveredCourse(course)}
                        >
                          <a className="course-card" href={course.href}>
                            <span className="course-card-index">{`0${index + 1}`}</span>
                            <h4 className="course-card-title">{course.title}</h4>
                            <span className="course-card-arrow" aria-hidden="true">→</span>
                          </a>
                        </li>
                      ))}
                    </ul>

                    <div className="courses-mega-preview" key={hoveredCourse.href}>
                      <div className="courses-mega-preview-image">
                        <img src={hoveredCourse.image} alt={hoveredCourse.imageAlt} />
                        <span className="course-card-tag">{hoveredCourse.imageTag}</span>
                      </div>

                      <span className="course-card-badge">
                        <span className="course-card-badge-dot" aria-hidden="true" />
                        {hoveredCourse.badge}
                      </span>

                      <h3 className="courses-mega-preview-title">{hoveredCourse.title}</h3>

                      <div className="course-card-meta">
                        {hoveredCourse.meta.map((item, index) => {
                          const Icon = COURSE_ICONS[item.icon];
                          return (
                            <span className="course-card-meta-item" key={index}>
                              <Icon /> {item.text}
                            </span>
                          );
                        })}
                      </div>

                      <p className="course-card-desc">{hoveredCourse.desc}</p>

                      <a
                        className="course-card-btn course-card-btn--block"
                        href={hoveredCourse.href}
                      >
                        {hoveredCourse.cta} →
                      </a>
                    </div>
                  </div>
                </li>

                {/* <li>
                  <a
                    href="/contactus"
                    className={isActive("/contactus") ? "active-link" : undefined}
                  >
                    Contact us
                  </a>
                </li> */}
                <li>
                  <a href="/jobs" className={isActive("/jobs") ? "active-link" : undefined}>
                    Jobs
                  </a>
                </li>
                <li>
                  <a href="/hire" className={isActive("/hire") ? "active-link" : undefined}>
                    Hire Talent
                  </a>
                </li>
                <li className="enroll-menu">
                  <a href="/enroll" className={isActive("/enroll") ? "active-link" : undefined}>
                    Enroll
                  </a>
                </li>
              </ul>

              {/* ── RIGHT: Login + Register buttons ── */}
              <div className="col-sm-4 col-lg-4">
                <ul className="right-side-header-ul">
                  <li className="login-option">
                    <button className="button-enroll-2" type="button">
                      Login
                    </button>
                    <ul className="login-options">
                      <a href={portalLink("/company-login")}>
                        <li>Employer</li>
                      </a>
                      <a href={portalLink("/student-login")}>
                        <li>Job seeker</li>
                      </a>
                      <a href={portalLink("/college-login")}>
                        <li>college</li>
                      </a>
                      <li className="login-dropdown">
                        <a
                          href="#learning-portal"
                          onClick={(event) => event.preventDefault()}
                        >
                          Learning Portal
                        </a>
                        <ul className="login-submenu">
                          <li>
                            <a
                              href="#student-portal"
                              onClick={(event) => openComingSoon(event, "student")}
                            >
                              Student
                            </a>
                          </li>
                          <li>
                            <a
                              href="#trainer-portal"
                              onClick={(event) => openComingSoon(event, "trainer")}
                            >
                              Trainer
                            </a>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </li>
                  <li className="login-option register-option">
                    <button className="login" type="button">
                      Register
                    </button>
                    <ul className="login-options">
                      <a href={portalLink("/company-register")}>
                        <li>Employer</li>
                      </a>
                      <a href={portalLink("/student-register")}>
                        <li>Job seeker</li>
                      </a>
                      <a href={portalLink("/college-register")}>
                        <li>College</li>
                      </a>
                    </ul>
                  </li>
                </ul>
              </div>

            </div>
          </div>

          {/* ── Mobile header bar ── */}
          <div className="wrapper-2">
            <div className="row myrow mobile-row">
              <div className="mobile-logo">
                <a href="/">
                  <p className="logo-text">IPD<span>I</span></p>
                </a>
              </div>

              <button
                ref={mobileProgramButtonRef}
                className="mobile-program-button"
                type="button"
                aria-expanded={mobileProgramsOpen}
                aria-controls="mobile-program-panel"
                onPointerDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  setMobileOpen(false);
                  setMobileProgramsOpen((open) => !open);
                }}
              >
                Programs
                <span className="chevron-wrap" aria-hidden="true">
                  <svg
                    width="9"
                    height="6"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M1 1L6.15152 7L11 1" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </span>
              </button>

              <div className="mobile-menu">
                <button
                  ref={mobileButtonRef}
                  type="button"
                  className="mobile-menu-button"
                  aria-label={mobileOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileOpen}
                  onPointerDown={(event) => event.stopPropagation()}
                  onTouchStart={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    setMobileProgramsOpen(false);
                    setMobileOpen((open) => !open);
                  }}
                >
                  <img
                    src={mobileOpen ? "/assets/img/wrong.svg" : "/assets/img/menu.svg"}
                    alt=""
                  />
                </button>
              </div>
            </div>
          </div>

          {mobileProgramsOpen && (
            <div
              ref={mobileProgramPanelRef}
              id="mobile-program-panel"
              className="mobile-program-panel"
              onPointerDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mobile-program-heading">All Programs</div>
              <ul className="mobile-program-list">
                <li>
                  <a href="/course-executive-architect" onClick={closeMobileMenu}>
                    <span className="mobile-program-title">Executive Architect</span>
                    <span className="mobile-program-arrow" aria-hidden="true">{">"}</span>
                  </a>
                </li>
                <li>
                  <a href="/course-executive-interior" onClick={closeMobileMenu}>
                    <span className="mobile-program-title">Executive Interior Designer</span>
                    <span className="mobile-program-arrow" aria-hidden="true">{">"}</span>
                  </a>
                </li>
                <li>
                  <a href="/customise-your-program" onClick={closeMobileMenu}>
                    <span className="mobile-program-title">Customize Your Program</span>
                    <span className="mobile-program-arrow" aria-hidden="true">{">"}</span>
                  </a>
                </li>
              </ul>
            </div>
          )}

          {/* ── Mobile sidebar ── */}
          {mobileOpen && (
            <div
              ref={mobileSidebarRef}
              className="wrapper-3"
              onPointerDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              <ul>
                <li className="mobile-auth-top">
                  <button
                    className="button-enroll-3"
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      setLoginOpen(true);
                    }}
                  >
                    <span className="mobile-auth-icon" aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M20.5 21C20.5 17.134 16.6944 14 12 14C7.30558 14 3.5 17.134 3.5 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </span>
                    Login
                  </button>
                  <button
                    className="button-register-3"
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      setRegisterOpen(true);
                    }}
                  >
                    Register
                    <span className="mobile-auth-icon" aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        <path d="M13 6L19 12L13 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>
                </li>
                {/* <li>
                  <button
                    onClick={() => {
                      closeMobileMenu();
                      window.location.assign("/enroll");
                    }}
                    className="button-enroll-2"
                    type="button"
                  >
                    Enroll
                  </button>
                </li> */}
                {/* <li>Courses</li> */}

<li className="mobile-courses-heading"  style={{fontWeight: '400',fontSize:"17px" }}  >Courses</li>

                <hr />
                <li onClick={closeMobileMenu}>
                  <a href="/course-executive-architect">
                    <div className="courses-1">Executive Architect</div>
                  </a>
                </li>
                <li onClick={closeMobileMenu}>
                  <a href="/course-executive-interior">
                    <div className="courses-1">Executive Interior Designer</div>
                  </a>
                </li>
                <li onClick={closeMobileMenu}>
                  <a href="/customise-your-program">
                    <div className="courses-1">Customize Your Program</div>
                  </a>
                </li>

                <hr />

                <li>
                  <a onClick={closeMobileMenu} href="/jobs">Jobs</a>
                </li>
                <li onClick={closeMobileMenu}>
                  <a href="/hire">Hire Talent</a>
                </li>

                <li onClick={closeMobileMenu}>
                  <a href="/trainer-application"> Become a Mentor</a>
                </li>


                <li onClick={closeMobileMenu}>
                  <a href="/contactus">Contact us</a>
                </li>

                <li onClick={closeMobileMenu}>
                  <a href="/enroll">
                 Enroll
                  </a>
                </li>

              </ul>
            </div>
          )}
        </div>
        <div className="header-spacer" />
      </header>

      {/* ── Mobile login dialog ── */}
      {loginOpen && (
        <div className="mobile-login-backdrop" onClick={() => setLoginOpen(false)}>
          <div
            className="mobile-login-dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <ul className="mobile-login-menu">
              <li>
                {/* <a href={portalLink("portal/company-login")}>Employer</a> */}

<a href={portalLink("/company-login")}>Employer</a>


              </li>
              <li>
                <a href={portalLink("/student-login")}>Job seeker</a>
              </li>
              <li>
                <a href={portalLink("/college-login")}>College</a>
              </li>
              <li className="learning-parent">
                <button
                  type="button"
                  onClick={() => setLearningOpen((open) => !open)}
                >
                  Learning Portal
                </button>
                {learningOpen && (
                  <ul className="learning-submenu">
                    <li>
                      <a
                        href={portalLink("/student-portal")}
                        onClick={(event) => openComingSoon(event, "student")}
                      >
                        Student
                      </a>
                    </li>
                    <li>
                      <a
                        href={portalLink("/trainer-portal")}
                        onClick={(event) => openComingSoon(event, "trainer")}
                      >
                        Trainer
                      </a>
                    </li>
                  </ul>
                )}
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* ── Mobile register dialog ── */}
      {registerOpen && (
        <div className="mobile-login-backdrop" onClick={() => setRegisterOpen(false)}>
          <div
            className="mobile-login-dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <ul className="mobile-login-menu">
              <li>
                <a href={portalLink("/company-register")}>Employer</a>
              </li>
              <li>
                <a href={portalLink("/student-register")}>Job seeker</a>
              </li>
              <li>
                <a href={portalLink("/college-register")}>College</a>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* ── Coming soon dialog ── */}
      {comingSoon && (
        <div
          className="mobile-login-backdrop"
          onClick={() => setComingSoon(null)}
        >
          <div
            className="coming-soon-dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="coming-soon-close"
              onClick={() => setComingSoon(null)}
              aria-label="Close"
            >
              ×
            </button>
            <h2>{comingSoon.title}</h2>
            <p>{comingSoon.text}</p>
          </div>
        </div>
      )}
    </>
  );
}

export default Header;